/**
 * Pure role-access logic with NO dependency on UserAPI / StorageAPI / Configuration.
 * Keep it that way — Configuration.ts imports from here, creating a one-way chain.
 *
 * useRoleAccess.ts is the React layer on top of this file.
 */

// ─── Role detection helpers ──────────────────────────────────────────────────

export const hasRoleAssignment = (
  authentication: any,
  roleId: string,
): boolean => {
  if (!authentication?.roleAssignments) return false;

  return authentication.roleAssignments.some((ra: any) => {
    const role = ra.role;
    if (!role) return false;

    const id =
      typeof role.getRoleID === "function" ? role.getRoleID() : role?.id;

    if (typeof id !== "string") return false;
    return id === roleId || id.startsWith(`${roleId}:`);
  });
};

export const isSystemAdminUser = (authentication: any): boolean =>
  authentication?.roles?.includes("administrator") ||
  authentication?.roles?.includes("root") ||
  false;

export const isRootUser = (authentication: any): boolean =>
  authentication?.roles?.includes("root") || false;

// ─── Public interface ────────────────────────────────────────────────────────

export interface RoleAccess {
  // Role flags
  isSystemAdmin: boolean;
  isRootAdmin: boolean;
  isCustomerAdmin: boolean;
  isBillingAdmin: boolean;
  isTechnicalAdmin: boolean;
  hasFullAccess: boolean;

  // Page / feature access
  canSeeCompanyProfile: boolean;
  canSeeTeamManagement: boolean;
  canManageRoles: boolean;
  canSeeBilling: boolean;
  canSeeStatistics: boolean;
  canSeeOrders: boolean;
  canPlaceOrders: boolean;
  canSeeApiKeys: boolean;
  canSeeAgent: boolean;
  canSeeProjects: boolean;
  canSeeProductManagement: boolean;
  canSeeFileManagement: boolean;
  canSeeOperations: boolean;
  canSeeDevelopment: boolean;
  canSeeDataManagement: boolean;
  /** Matches server PROCESS_INSTANCES_DELETE (customer admin + system admin). */
  canDeleteProcessInstances: boolean;

  // Customer data (populated asynchronously by the hook; null while loading)
  customer: any | null;
}

/** Role label from a BPMN human task row (`role` string or `{ name }`). */
const getHumanTaskRoleLabel = (task: any): string => {
  const r = task?.role;
  if (r == null) return "";
  if (typeof r === "string") return String(r).trim();
  if (typeof r === "object" && r.name != null) return String(r.name).trim();
  return "";
};

/**
 * True if the signed-in user may act on this human task: status is actionable, the task defines
 * a `role`, and **some role assignment** matches that requirement (by role name or id).
 */
export const viewerMayExecuteHumanTask = (
  authentication: any,
  task: any,
): boolean => {
  const st = String(task?.status ?? "").toUpperCase();
  if (st === "COMPLETED" || st === "DONE" || st === "CLOSED") return false;
  const required = getHumanTaskRoleLabel(task);
  if (!required) return false;
  if (!authentication?.roleAssignments?.length) return false;
  const reqLower = required.toLowerCase();
  return authentication.roleAssignments.some((ra: any) => {
    const role = ra?.role;
    if (!role) return false;
    const idRaw =
      typeof role.getRoleID === "function" ? role.getRoleID() : role?.id;
    const nameRaw =
      typeof role.getRoleName === "function" ? role.getRoleName() : role?.name;
    const idStr = idRaw != null ? String(idRaw).toLowerCase() : "";
    const nameStr = nameRaw != null ? String(nameRaw).toLowerCase() : "";
    if (nameStr === reqLower || idStr === reqLower) return true;
    if (idStr.includes(":")) {
      const base = idStr.split(":")[0];
      if (base === reqLower) return true;
    }
    return false;
  });
};

// ─── Shared resolution logic ─────────────────────────────────────────────────

/**
 * Resolves role access flags from authentication + optional customer data.
 * `customer` is null while the async fetch is in-flight.
 */
export const resolveAccess = (
  authentication: any,
  customer: any | null = null,
): RoleAccess => {
  const isSystemAdmin = isSystemAdminUser(authentication);
  const isRootAdmin = isRootUser(authentication);
  const isCustomerAdmin = hasRoleAssignment(
    authentication,
    "customerAdministrator",
  );
  const isBillingAdmin = hasRoleAssignment(
    authentication,
    "billingAdministrator",
  );
  const isTechnicalAdmin = hasRoleAssignment(
    authentication,
    "technicalAdministrator",
  );

  const hasFullAccess = isSystemAdmin || isCustomerAdmin;

  return {
    // ── Role flags ───────────────────────────────────────────────────────
    isSystemAdmin,
    isRootAdmin,
    isCustomerAdmin,
    isBillingAdmin,
    isTechnicalAdmin,
    hasFullAccess,

    // ── Customer data ────────────────────────────────────────────────────
    customer,

    // ── Access matrix ────────────────────────────────────────────────────
    canSeeCompanyProfile: hasFullAccess,
    canSeeTeamManagement: hasFullAccess,
    canManageRoles: hasFullAccess,

    canSeeBilling: hasFullAccess || isBillingAdmin,

    canSeeStatistics: hasFullAccess || isTechnicalAdmin,

    canSeeOrders: hasFullAccess || isBillingAdmin || isTechnicalAdmin,
    canPlaceOrders: hasFullAccess,

    canSeeApiKeys: hasFullAccess || isTechnicalAdmin,
    canSeeAgent: hasFullAccess || isTechnicalAdmin,

    canSeeProjects: hasFullAccess,
    canSeeProductManagement: hasFullAccess,
    // Not gated on the storage provider any more. It used to be hidden for SharePoint customers
    // because the file manager only worked against S3 — its upload wrote straight into the S3
    // bucket and the metadata upsert then failed its `exists()` check, so a SharePoint tenant got
    // nothing but errors. The upload now goes through the storage abstraction and the manager
    // browses whichever volumes the customer has, so there is nothing left to hide from.
    canSeeFileManagement: hasFullAccess,
    canSeeOperations: hasFullAccess || isTechnicalAdmin,
    canSeeDevelopment: hasFullAccess,
    canSeeDataManagement: hasFullAccess,
    canDeleteProcessInstances: hasFullAccess,
  };
};

/**
 * Plain function – use where hooks are not allowed
 * (callbacks, Configuration.ts, etc.).
 */
export const getRoleAccess = (authentication: any): RoleAccess =>
  resolveAccess(authentication, null);
